import { io, Socket } from "socket.io-client";
import { currentEnv } from "../../Api/configs";
import {
  JoinEventPayload,
  StreamStartPayload,
  StreamDisconnectPayload,
  StreamReconnectPayload,
  StreamStopPayload,
  StreamEventBroadcast,
  ResultBroadcast,
  VotingEnabledBroadcast,
  VotingCountBroadcast,
  SOCKET_EVENTS,
  UserRole,
} from "./types";

type EventCallback<T> = (data: T) => void;

interface EventListeners {
  [SOCKET_EVENTS.STREAM_STARTED]: Set<EventCallback<StreamEventBroadcast>>;
  [SOCKET_EVENTS.STREAM_DISCONNECTED]: Set<EventCallback<StreamEventBroadcast>>;
  [SOCKET_EVENTS.STREAM_RECONNECTED]: Set<EventCallback<StreamEventBroadcast>>;
  [SOCKET_EVENTS.STREAM_STOPPED]: Set<EventCallback<StreamEventBroadcast>>;
  [SOCKET_EVENTS.RESULT]: Set<EventCallback<ResultBroadcast>>;
  [SOCKET_EVENTS.VOTING_ENABLED]: Set<EventCallback<VotingEnabledBroadcast>>;
  [SOCKET_EVENTS.VOTING_COUNT]: Set<EventCallback<VotingCountBroadcast>>;
}

/**
 * EventSocketClient - Singleton Socket.io client for event streaming
 */
class EventSocketClient {
  private socket: Socket | null = null;
  private eventId: string | null = null;
  private userId: string | null = null;
  private role: UserRole | null = null;
  private listeners: EventListeners;
  private connectionListeners: Set<(connected: boolean) => void> = new Set();
  private errorListeners: Set<(error: string) => void> = new Set();

  constructor() {
    this.listeners = {
      [SOCKET_EVENTS.STREAM_STARTED]: new Set(),
      [SOCKET_EVENTS.STREAM_DISCONNECTED]: new Set(),
      [SOCKET_EVENTS.STREAM_RECONNECTED]: new Set(),
      [SOCKET_EVENTS.STREAM_STOPPED]: new Set(),
      [SOCKET_EVENTS.RESULT]: new Set(),
      [SOCKET_EVENTS.VOTING_ENABLED]: new Set(),
      [SOCKET_EVENTS.VOTING_COUNT]: new Set(),
    };
  }

  /**
   * Connect to event socket room
   */
  connect(
    eventId: string,
    userId: string,
    role: UserRole,
    accessToken?: string
  ): void {
    // Disconnect if already connected
    if (this.socket?.connected) {
      this.disconnect();
    }

    this.eventId = eventId;
    this.userId = userId;
    this.role = role;

    const socketUrl = currentEnv.eventSocketUrl;
    console.log("[EventSocket] Connecting to:", socketUrl);

    this.socket = io(`${socketUrl}/events`, {
      transports: ["websocket", "polling"], // Fallback to polling if websocket fails
      auth: accessToken ? { accessToken } : undefined,
      // Production-ready reconnection settings for long-duration streams
      reconnection: true,
      reconnectionAttempts: Infinity, // Keep trying forever
      reconnectionDelay: 1000, // Start with 1s delay
      reconnectionDelayMax: 10000, // Max 10s between attempts
      randomizationFactor: 0.5, // Add jitter to prevent thundering herd
      // Timeout settings
      timeout: 20000, // 20s connection timeout
    });

    this.setupEventListeners();
  }

  /**
   * Setup all socket event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on("connect", () => {
      console.log("[EventSocket] Connected");
      this.notifyConnectionListeners(true);

      // Join the event room
      if (this.eventId && this.userId && this.role) {
        const joinPayload: JoinEventPayload = {
          eventId: this.eventId,
          userId: this.userId,
          role: this.role,
        };
        this.socket?.emit(
          SOCKET_EVENTS.JOIN,
          joinPayload,
          (response: { success: boolean; room: string }) => {
            console.log("[EventSocket] Joined room:", response);
          }
        );
      }
    });

    this.socket.on("disconnect", (reason) => {
      console.log("[EventSocket] Disconnected:", reason);
      this.notifyConnectionListeners(false);
      // Log the reason for debugging
      if (reason === "io server disconnect") {
        // Server disconnected us, need to manually reconnect
        console.log(
          "[EventSocket] Server disconnected, attempting reconnect..."
        );
        this.socket?.connect();
      }
    });

    // Reconnection events for user feedback
    this.socket.io.on("reconnect_attempt", (attempt) => {
      console.log(`[EventSocket] Reconnection attempt #${attempt}`);
    });

    this.socket.io.on("reconnect", (attempt) => {
      console.log(`[EventSocket] Reconnected after ${attempt} attempts`);
      this.notifyConnectionListeners(true);
    });

    this.socket.io.on("reconnect_error", (error) => {
      console.error("[EventSocket] Reconnection error:", error.message);
    });

    this.socket.io.on("reconnect_failed", () => {
      console.error("[EventSocket] Reconnection failed after all attempts");
      this.notifyErrorListeners("Connection lost. Please refresh the page.");
    });

    this.socket.on("connect_error", (error) => {
      console.error("[EventSocket] Connection error:", error.message);
      this.notifyErrorListeners(error.message);
    });

    // Stream events
    this.socket.on(
      SOCKET_EVENTS.STREAM_STARTED,
      (data: StreamEventBroadcast) => {
        console.log("[EventSocket] Stream started:", data);
        this.listeners[SOCKET_EVENTS.STREAM_STARTED].forEach((cb) => cb(data));
      }
    );

    this.socket.on(
      SOCKET_EVENTS.STREAM_DISCONNECTED,
      (data: StreamEventBroadcast) => {
        console.log("[EventSocket] Stream disconnected:", data);
        this.listeners[SOCKET_EVENTS.STREAM_DISCONNECTED].forEach((cb) =>
          cb(data)
        );
      }
    );

    this.socket.on(
      SOCKET_EVENTS.STREAM_RECONNECTED,
      (data: StreamEventBroadcast) => {
        console.log("[EventSocket] Stream reconnected:", data);
        this.listeners[SOCKET_EVENTS.STREAM_RECONNECTED].forEach((cb) =>
          cb(data)
        );
      }
    );

    this.socket.on(
      SOCKET_EVENTS.STREAM_STOPPED,
      (data: StreamEventBroadcast) => {
        console.log("[EventSocket] Stream stopped:", data);
        this.listeners[SOCKET_EVENTS.STREAM_STOPPED].forEach((cb) => cb(data));
      }
    );

    // Result events
    this.socket.on(SOCKET_EVENTS.RESULT, (data: ResultBroadcast) => {
      console.log("[EventSocket] Result:", data);
      this.listeners[SOCKET_EVENTS.RESULT].forEach((cb) => cb(data));
    });

    // Voting events
    this.socket.on(
      SOCKET_EVENTS.VOTING_ENABLED,
      (data: VotingEnabledBroadcast) => {
        console.log("[EventSocket] Voting enabled:", data);
        this.listeners[SOCKET_EVENTS.VOTING_ENABLED].forEach((cb) => cb(data));
      }
    );

    this.socket.on(SOCKET_EVENTS.VOTING_COUNT, (data: VotingCountBroadcast) => {
      console.log("[EventSocket] Vote count:", data);
      this.listeners[SOCKET_EVENTS.VOTING_COUNT].forEach((cb) => cb(data));
    });
  }

  /**
   * Disconnect from socket
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.emit(SOCKET_EVENTS.LEAVE);
      this.socket.disconnect();
      this.socket = null;
    }
    this.eventId = null;
    this.userId = null;
    this.role = null;
    console.log("[EventSocket] Disconnected");
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  // ============================================
  // Emit Methods (for streamers)
  // ============================================

  emitStreamStart(username: string, role: "organiser" | "challenger"): void {
    if (!this.socket?.connected) {
      console.warn("[EventSocket] Cannot emit: not connected");
      return;
    }
    const payload: StreamStartPayload = { username, role };
    this.socket.emit(SOCKET_EVENTS.STREAM_START, payload);
    console.log("[EventSocket] Emitted stream:start", payload);
  }

  emitStreamDisconnect(
    username: string,
    role: "organiser" | "challenger",
    reason?: string
  ): void {
    if (!this.socket?.connected) {
      console.warn("[EventSocket] Cannot emit: not connected");
      return;
    }
    const payload: StreamDisconnectPayload = { username, role, reason };
    this.socket.emit(SOCKET_EVENTS.STREAM_DISCONNECT, payload);
    console.log("[EventSocket] Emitted stream:disconnect", payload);
  }

  emitStreamReconnect(
    username: string,
    role: "organiser" | "challenger"
  ): void {
    if (!this.socket?.connected) {
      console.warn("[EventSocket] Cannot emit: not connected");
      return;
    }
    const payload: StreamReconnectPayload = { username, role };
    this.socket.emit(SOCKET_EVENTS.STREAM_RECONNECT, payload);
    console.log("[EventSocket] Emitted stream:reconnect", payload);
  }

  emitStreamStop(username: string, role: "organiser" | "challenger"): void {
    if (!this.socket?.connected) {
      console.warn("[EventSocket] Cannot emit: not connected");
      return;
    }
    const payload: StreamStopPayload = { username, role };
    this.socket.emit(SOCKET_EVENTS.STREAM_STOP, payload);
    console.log("[EventSocket] Emitted stream:stop", payload);
  }

  // ============================================
  // Listener Registration
  // ============================================

  onStreamStarted(callback: EventCallback<StreamEventBroadcast>): () => void {
    this.listeners[SOCKET_EVENTS.STREAM_STARTED].add(callback);
    return () => this.listeners[SOCKET_EVENTS.STREAM_STARTED].delete(callback);
  }

  onStreamDisconnected(
    callback: EventCallback<StreamEventBroadcast>
  ): () => void {
    this.listeners[SOCKET_EVENTS.STREAM_DISCONNECTED].add(callback);
    return () =>
      this.listeners[SOCKET_EVENTS.STREAM_DISCONNECTED].delete(callback);
  }

  onStreamReconnected(
    callback: EventCallback<StreamEventBroadcast>
  ): () => void {
    this.listeners[SOCKET_EVENTS.STREAM_RECONNECTED].add(callback);
    return () =>
      this.listeners[SOCKET_EVENTS.STREAM_RECONNECTED].delete(callback);
  }

  onStreamStopped(callback: EventCallback<StreamEventBroadcast>): () => void {
    this.listeners[SOCKET_EVENTS.STREAM_STOPPED].add(callback);
    return () => this.listeners[SOCKET_EVENTS.STREAM_STOPPED].delete(callback);
  }

  onResult(callback: EventCallback<ResultBroadcast>): () => void {
    this.listeners[SOCKET_EVENTS.RESULT].add(callback);
    return () => this.listeners[SOCKET_EVENTS.RESULT].delete(callback);
  }

  onVotingEnabled(callback: EventCallback<VotingEnabledBroadcast>): () => void {
    this.listeners[SOCKET_EVENTS.VOTING_ENABLED].add(callback);
    return () => this.listeners[SOCKET_EVENTS.VOTING_ENABLED].delete(callback);
  }

  onVotingCount(callback: EventCallback<VotingCountBroadcast>): () => void {
    this.listeners[SOCKET_EVENTS.VOTING_COUNT].add(callback);
    return () => this.listeners[SOCKET_EVENTS.VOTING_COUNT].delete(callback);
  }

  // Connection state listeners
  onConnectionChange(callback: (connected: boolean) => void): () => void {
    this.connectionListeners.add(callback);
    return () => this.connectionListeners.delete(callback);
  }

  onError(callback: (error: string) => void): () => void {
    this.errorListeners.add(callback);
    return () => this.errorListeners.delete(callback);
  }

  private notifyConnectionListeners(connected: boolean): void {
    this.connectionListeners.forEach((cb) => cb(connected));
  }

  private notifyErrorListeners(error: string): void {
    this.errorListeners.forEach((cb) => cb(error));
  }
}

// Singleton instance
export const eventSocketClient = new EventSocketClient();
